import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation } from '@apollo/client';
import {
  ADD_PARTICIPANT,
  BIND_PARTICIPANT_TO_ROLE,
  GET_PLAN,
  RESEND_PARTICIPANT_INVITE,
} from '../graphql/queries';
import { Participant, PlanRole, InviteStatus } from '../types/generated/graphql';
import { colors } from '../theme/colors';
import GravatarAvatar from './GravatarAvatar';

interface RoleAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  planId: string;
  role: PlanRole | null;
  participants: Participant[];
}

type InputMode = 'select' | 'new';

export const RoleAssignmentDialog: React.FC<RoleAssignmentDialogProps> = ({
  open,
  onClose,
  planId,
  role,
  participants,
}) => {
  const [mode, setMode] = useState<InputMode>('select');
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [addParticipant, { loading: addingParticipant }] = useMutation(ADD_PARTICIPANT, {
    refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
  });

  const [bindParticipantToRole, { loading: bindingRole }] = useMutation(
    BIND_PARTICIPANT_TO_ROLE,
    {
      refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    }
  );

  const [resendInvite, { loading: resendingInvite }] = useMutation(
    RESEND_PARTICIPANT_INVITE,
    {
      refetchQueries: [{ query: GET_PLAN, variables: { id: planId } }],
    }
  );

  const loading = addingParticipant || bindingRole || resendingInvite;

  // Filter out participants already assigned to a role
  const availableParticipants = useMemo(() => {
    return participants;
  }, [participants]);

  const handleReset = () => {
    setMode('select');
    setSelectedParticipantId(null);
    setNewName('');
    setNewEmail('');
    setNewPhone('');
    setError(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSelectParticipant = (participantId: string) => {
    setSelectedParticipantId(participantId);
    setMode('select');
  };

  const handleResendInvite = async (participantId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setError(null);
    try {
      await resendInvite({
        variables: { planId, participantId },
      });
    } catch (err: any) {
      setError(err.message || 'Failed to resend invite');
    }
  };

  const getInviteStatusColor = (status?: InviteStatus | null): string => {
    switch (status) {
      case InviteStatus.Accepted:
        return colors.green[600];
      case InviteStatus.Sent:
        return colors.blue[500];
      case InviteStatus.Expired:
        return colors.red[600];
      default:
        return colors.gray[400];
    }
  };

  const getInviteStatusLabel = (status?: InviteStatus | null): string => {
    switch (status) {
      case InviteStatus.Accepted:
        return 'Joined';
      case InviteStatus.Sent:
        return 'Invited';
      case InviteStatus.Expired:
        return 'Expired';
      default:
        return 'Not invited';
    }
  };

  const handleAssign = async () => {
    if (!role) return;
    setError(null);

    try {
      if (mode === 'select' && selectedParticipantId) {
        // Bind existing participant to role
        await bindParticipantToRole({
          variables: {
            planId,
            roleId: role.roleId,
            personId: selectedParticipantId,
          },
        });
        handleClose();
      } else if (mode === 'new') {
        // Validate input
        if (!newName.trim()) {
          setError('Please enter a name');
          return;
        }
        if (!newEmail.trim() && !newPhone.trim()) {
          setError('Please enter an email address or phone number');
          return;
        }

        // Create new participant
        const { data } = await addParticipant({
          variables: {
            planId,
            participant: {
              name: newName.trim(),
              email: newEmail.trim() || null,
              phone: newPhone.trim() || null,
            },
          },
        });

        // Find the newly created participant
        const newParticipant = data?.addParticipant?.participants?.find(
          (p: Participant) =>
            p.email?.toLowerCase() === newEmail.trim().toLowerCase() ||
            p.phone === newPhone.trim()
        );

        if (newParticipant) {
          // Bind the new participant to the role
          await bindParticipantToRole({
            variables: {
              planId,
              roleId: role.roleId,
              personId: newParticipant.id,
            },
          });
        }

        handleClose();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to assign role');
    }
  };

  if (!role) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Assign Role
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Who will be the <strong>{role.name}</strong>?
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Existing participants */}
        {availableParticipants.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, display: 'block', mb: 1 }}
            >
              Select from existing participants
            </Typography>
            <List dense sx={{ bgcolor: colors.gray[50], borderRadius: 1 }}>
              {availableParticipants.map((participant) => (
                <ListItemButton
                  key={participant.id}
                  selected={selectedParticipantId === participant.id && mode === 'select'}
                  onClick={() => handleSelectParticipant(participant.id)}
                  sx={{
                    borderRadius: 1,
                    '&.Mui-selected': {
                      bgcolor: colors.blue[100],
                    },
                  }}
                >
                  <ListItemAvatar>
                    <GravatarAvatar
                      email={participant.email}
                      name={participant.name}
                      size={32}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={participant.name}
                    secondary={participant.email || participant.phone || 'No contact'}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Chip
                    label={getInviteStatusLabel(participant.inviteStatus)}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: `${getInviteStatusColor(participant.inviteStatus)}15`,
                      color: getInviteStatusColor(participant.inviteStatus),
                    }}
                  />
                  {/* Resend invite button */}
                  {participant.email &&
                   (participant.inviteStatus === InviteStatus.Sent ||
                    participant.inviteStatus === InviteStatus.Expired ||
                    !participant.inviteStatus) && (
                    <Tooltip title="Resend invite email">
                      <IconButton
                        size="small"
                        onClick={(e) => handleResendInvite(participant.id, e)}
                        disabled={resendingInvite}
                        sx={{
                          ml: 0.5,
                          p: 0.5,
                          color: colors.blue[600],
                          '&:hover': { bgcolor: colors.blue[50] },
                        }}
                      >
                        {resendingInvite ? (
                          <CircularProgress size={16} />
                        ) : (
                          <RefreshIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  {selectedParticipantId === participant.id && mode === 'select' && (
                    <CheckIcon sx={{ ml: 1, color: colors.blue[600], fontSize: 20 }} />
                  )}
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}

        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">
            OR
          </Typography>
        </Divider>

        {/* Add new participant */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 1 }}
          >
            <PersonAddIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
            Add new person
          </Typography>

          <TextField
            fullWidth
            size="small"
            label="Name"
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              setMode('new');
            }}
            placeholder="John Doe"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            size="small"
            label="Email address"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              setMode('new');
            }}
            placeholder="john@example.com"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ fontSize: 18, color: colors.gray[400] }} />
                </InputAdornment>
              ),
            }}
            helperText="They will receive an invitation email to join the plan"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            size="small"
            label="Phone number (optional)"
            value={newPhone}
            onChange={(e) => {
              setNewPhone(e.target.value);
              setMode('new');
            }}
            placeholder="+1 555 123 4567"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ fontSize: 18, color: colors.gray[400] }} />
                </InputAdornment>
              ),
            }}
            helperText="SMS invitations coming soon"
            disabled
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={
            loading ||
            (mode === 'select' && !selectedParticipantId) ||
            (mode === 'new' && (!newName.trim() || !newEmail.trim()))
          }
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Assigning...' : mode === 'new' ? 'Add & Assign' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleAssignmentDialog;
